import React, { useState } from "react";

const colorGroups = {
  primary: [
    "color-primary",
    "color-primary-darker",
    "color-primary-darkest",
    "color-primary-light",
    "color-primary-light-darker",
    "color-primary-hover",
  ],
  gray: [
    "color-gray-10",
    "color-gray-20",
    "color-gray-30",
    "color-gray-40",
    "color-gray-50",
    "color-gray-60",
    "color-gray-70",
    "color-gray-80",
    "color-gray-85",
    "color-gray-90",
    "color-gray-100",
  ],
  surface: [
    "color-surface-high",
    "color-surface-mid",
    "color-surface-low",
    "color-surface-lowest",
    "color-on-surface",
    "color-surface-primary-container",
    "color-on-primary-container",
  ],
  semantic: [
    "color-warning",
    "color-warning-dark",
    "color-warning-darker",
    "color-warning-darkest",
    "color-warning-background",
    "color-danger",
    "color-danger-dark",
    "color-danger-darker",
    "color-danger-darkest",
    "color-danger-background",
    "color-success",
    "color-success-darker",
    "color-success-darkest",
    "color-success-contrast",
  ],
  brand: [
    "color-brand-hover",
    "color-brand-active",
    "color-border-outline",
    "color-border-outline-variant",
  ],
  ui: [
    "text-primary-color",
    "icon-fill-color",
    "default-bg-color",
    "input-bg-color",
    "input-border-color",
    "island-bg-color",
    "focus-highlight-color",
    "link-color",
    "sidebar-bg-color",
    "sidebar-border-color",
    "default-border-color",
  ],
  muted: [
    "color-muted",
    "color-muted-darker",
    "color-muted-darkest",
    "color-muted-background",
    "color-muted-background-darker",
    "color-disabled",
  ],
};

interface ColorCardProps {
  varName: string;
}

const ColorCard: React.FC<ColorCardProps> = ({ varName }) => {
  const [showCopied, setShowCopied] = useState(false);
  const cssVar = `--${varName}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(cssVar).then(() => {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1000);
    });
  };

  return (
    <div className="ds-color-card" onClick={copyToClipboard}>
      <div
        className="ds-color-preview"
        style={{ backgroundColor: `var(--${varName})` }}
      >
        <div className={`ds-copy-indicator ${showCopied ? "show" : ""}`}>
          Copied!
        </div>
      </div>
      <div className="ds-color-info">
        <h3>{cssVar}</h3>
        <div className="ds-color-value">
          {getComputedStyle(document.documentElement)
            .getPropertyValue(cssVar)
            .trim() || "Not defined"}
        </div>
      </div>
    </div>
  );
};

export const Colors: React.FC = () => {
  return (
    <div>
      <div className="ds-section-header">
        <h2>🎨 Color System</h2>
        <p className="ds-description">
          Click any color to copy its CSS variable name
        </p>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">Primary Colors</h3>
        <div className="ds-color-grid">
          {colorGroups.primary.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">Gray Scale</h3>
        <div className="ds-color-grid">
          {colorGroups.gray.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">Surface Colors</h3>
        <div className="ds-color-grid">
          {colorGroups.surface.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">Semantic Colors</h3>
        <div className="ds-color-grid">
          {colorGroups.semantic.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">Brand & Interactive</h3>
        <div className="ds-color-grid">
          {colorGroups.brand.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">UI Colors</h3>
        <div className="ds-color-grid">
          {colorGroups.ui.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>

      <div className="ds-color-section">
        <h3 className="ds-section-title">Muted & Disabled</h3>
        <div className="ds-color-grid">
          {colorGroups.muted.map((varName) => (
            <ColorCard key={varName} varName={varName} />
          ))}
        </div>
      </div>
    </div>
  );
};
